import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FlagIcon,
  CalendarIcon,
  UserGroupIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import { performanceGoalsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const PerformanceGoals = () => {
  const [goals, setGoals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    period: '',
    status: 'active',
    assignedTo: ''
  });

  const { user, hasRole } = useAuth();

  const goalTypes = [
    { value: 'leads', label: 'Leads', icon: UserGroupIcon },
    { value: 'customers', label: 'Customers', icon: UserGroupIcon },
    { value: 'revenue', label: 'Revenue', icon: ChartBarIcon },
    { value: 'followups', label: 'Follow-ups', icon: ClockIcon },
    { value: 'conversions', label: 'Conversions', icon: FlagIcon },
    { value: 'calls', label: 'Calls', icon: PhoneIcon },
    { value: 'meetings', label: 'Meetings', icon: CalendarIcon }
  ];

  const periods = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  useEffect(() => {
    fetchGoals();
    fetchUsers();
  }, [filters]);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await performanceGoalsAPI.getAll(filters);
      setGoals(response.data.goals);
    } catch (error) {
      console.error('Error fetching goals:', error);
      toast.error('Failed to load performance goals');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll({ status: 'active' });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleDeleteGoal = (goal) => {
    setGoalToDelete(goal);
    setShowDeleteModal(true);
  };

  const confirmDeleteGoal = async () => {
    if (!goalToDelete) return;

    try {
      await performanceGoalsAPI.delete(goalToDelete._id);
      toast.success('Goal deleted successfully');
      fetchGoals();
      setShowDeleteModal(false);
      setGoalToDelete(null);
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const getGoalTypeIcon = (type) => {
    const goalType = goalTypes.find(gt => gt.value === type);
    return goalType ? goalType.icon : FlagIcon;
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatPeriod = (period) => {
    return periods.find(p => p.value === period)?.label || period;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading performance goals..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 p-4 sm:p-6 lg:p-8 mb-6 lg:mb-8 shadow-2xl"
        >
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>

          <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <FlagIcon className="h-6 sm:h-8 w-6 sm:w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">Performance Goals</h1>
                <p className="text-purple-100 text-sm sm:text-base lg:text-lg">
                  Set and track performance targets for your team
                </p>
              </div>
            </div>
            {hasRole('company_admin', 'super_admin') && (
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent rounded-xl shadow-lg text-sm sm:text-base font-semibold text-white bg-white/20 backdrop-blur-sm hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Goal
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 mb-6 lg:mb-8"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative group">
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/50 border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md backdrop-blur-sm text-sm sm:text-base"
              >
                <option value="">All Types</option>
                {goalTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="relative group">
              <select
                value={filters.period}
                onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value }))}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/50 border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md backdrop-blur-sm text-sm sm:text-base"
              >
                <option value="">All Periods</option>
                {periods.map(period => (
                  <option key={period.value} value={period.value}>{period.label}</option>
                ))}
              </select>
            </div>

            <div className="relative group">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/50 border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md backdrop-blur-sm text-sm sm:text-base"
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
                <option value="">All Status</option>
              </select>
            </div>

            <div className="relative group">
              <select
                value={filters.assignedTo}
                onChange={(e) => setFilters(prev => ({ ...prev, assignedTo: e.target.value }))}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/50 border border-white/30 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md backdrop-blur-sm text-sm sm:text-base"
              >
                <option value="">All Users</option>
                <option value="">Company Goals</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Goals Grid */}
        {goals.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 sm:p-8 lg:p-12 text-center"
          >
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full blur-3xl opacity-20"></div>
              <div className="relative p-4 sm:p-6 bg-gradient-to-r from-purple-100 to-blue-100 rounded-2xl w-fit mx-auto">
                <FlagIcon className="h-12 sm:h-16 w-12 sm:w-16 lg:h-20 lg:w-20 text-purple-600" />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4">No performance goals found</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Get started by creating your first performance goal to track and motivate your team's achievements.
            </p>
            {hasRole('company_admin', 'super_admin') && (
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)" }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 border border-transparent rounded-2xl shadow-lg text-base sm:text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200"
              >
                <PlusIcon className="h-6 w-6 mr-3" />
                Create Goal
              </motion.button>
            )}
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {goals.map((goal, index) => {
              const progress = goal.currentValue / goal.targetValue * 100;
              const IconComponent = getGoalTypeIcon(goal.type);

              return (
                <motion.div
                  key={goal._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  whileHover={{ 
                    y: -8, 
                    scale: 1.02,
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  }}
                  className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/20 overflow-hidden hover:shadow-3xl transition-all duration-500 relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-transparent to-white/20 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-700"></div>

                  <div className="relative p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          className="p-2 sm:p-3 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl shadow-xl relative overflow-hidden"
                        >
                          <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <IconComponent className="h-5 sm:h-6 w-5 sm:w-6 text-white relative z-10" />
                        </motion.div>
                        <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 truncate">{goal.title}</h3>
                          <p className="text-sm text-gray-600 capitalize">{goal.type}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(goal.status)}`}>
                        {goal.status}
                      </span>
                    </div>

                    <div className="mb-6">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>Progress</span>
                        <span className="font-semibold">{goal.currentValue} / {goal.targetValue}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(progress, 100)}%` }}
                          transition={{ delay: 0.5 + index * 0.1, duration: 1, ease: "easeOut" }}
                          className={`h-3 rounded-full shadow-sm ${getProgressColor(progress)}`}
                        />
                      </div>
                      <div className="text-right text-sm font-bold text-gray-900 mt-2">
                        {progress.toFixed(1)}%
                      </div>
                    </div>

                    <div className="space-y-2 sm:space-y-3 text-sm text-gray-600">
                      <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50/50 rounded-xl backdrop-blur-sm">
                        <span className="font-medium">Period:</span>
                        <span className="capitalize font-semibold text-gray-900">{formatPeriod(goal.period)}</span>
                      </div>
                      <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50/50 rounded-xl backdrop-blur-sm">
                        <span className="font-medium">Assigned to:</span>
                        <span className="font-semibold text-gray-900 truncate ml-2">
                          {goal.assignedTo ? `${goal.assignedTo.firstName} ${goal.assignedTo.lastName}` : 'Company'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-2 sm:p-3 bg-gray-50/50 rounded-xl backdrop-blur-sm">
                        <span className="font-medium">Ends:</span>
                        <span className="font-semibold text-gray-900">{formatDate(goal.endDate)}</span>
                      </div>
                    </div>

                      <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6 pt-4 border-t border-gray-200/50">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setEditingGoal(goal)}
                          className="inline-flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <PencilIcon className="h-4 w-4 mr-2" />
                          Edit
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDeleteGoal(goal)}
                          className="inline-flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <TrashIcon className="h-4 w-4 mr-2" />
                          Delete
                        </motion.button>
                      </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || editingGoal) && (
          <GoalModal
            goal={editingGoal}
            users={users}
            goalTypes={goalTypes}
            periods={periods}
            onClose={() => {
              setShowCreateModal(false);
              setEditingGoal(null);
            }}
            onSave={() => {
              fetchGoals();
              setShowCreateModal(false);
              setEditingGoal(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && goalToDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDeleteModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                <div                 className="bg-gradient-to-r from-red-500 to-pink-500 px-4 sm:px-6 py-4 sm:py-6 rounded-t-3xl">
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                      <ExclamationTriangleIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-3">
                      Delete Performance Goal
                    </h3>
                    <p className="text-red-100 mb-4">
                      Are you sure you want to delete <strong className="text-white">"{goalToDelete.title}"</strong>?
                    </p>

                    <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-2xl p-4 mb-4">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-300 mr-3 flex-shrink-0" />
                        <div className="text-left">
                          <p className="text-white font-semibold">Warning!</p>
                          <p className="text-red-100 text-sm">This will permanently delete the goal and all its progress data. This action cannot be undone.</p>
                        </div>
                      </div>
                    </div>

                    <p className="text-red-200 text-sm">
                      This action is permanent and cannot be reversed.
                    </p>
                  </div>
                </div>

                <div className="px-4 sm:px-6 py-4 sm:py-6 bg-gray-50 rounded-b-3xl flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-4 sm:px-6 py-2 sm:py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-2xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={confirmDeleteGoal}
                    className="flex-1 px-4 sm:px-6 py-2 sm:py-3 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-pink-500 border border-transparent rounded-2xl hover:from-red-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Delete Goal
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
};

// Goal Modal Component
const GoalModal = ({ goal, users, goalTypes, periods, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    type: goal?.type || 'leads',
    title: goal?.title || '',
    targetValue: goal?.targetValue || 1,
    period: goal?.period || 'monthly',
    startDate: goal?.startDate ? new Date(goal.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    endDate: goal?.endDate ? new Date(goal.endDate).toISOString().split('T')[0] : '',
    priority: goal?.priority || 'medium',
    description: goal?.description || '',
    assignedTo: goal?.assignedTo?._id || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (goal) {
        await performanceGoalsAPI.update(goal._id, formData);
        toast.success('Goal updated successfully');
      } else {
        await performanceGoalsAPI.create(formData);
        toast.success('Goal created successfully');
      }
      onSave();
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error('Failed to save goal');
    } finally {
      setLoading(false);
    }
  };

  const calculateEndDate = (startDate, period) => {
    const start = new Date(startDate);
    let end = new Date(start);

    switch (period) {
      case 'daily':
        end.setDate(start.getDate() + 1);
        break;
      case 'weekly':
        end.setDate(start.getDate() + 7);
        break;
      case 'monthly':
        end.setMonth(start.getMonth() + 1);
        break;
      case 'quarterly':
        end.setMonth(start.getMonth() + 3);
        break;
      case 'yearly':
        end.setFullYear(start.getFullYear() + 1);
        break;
    }

    return end.toISOString().split('T')[0];
  };

  const handlePeriodChange = (period) => {
    setFormData(prev => ({
      ...prev,
      period,
      endDate: calculateEndDate(prev.startDate, period)
    }));
  };

  const handleStartDateChange = (startDate) => {
    setFormData(prev => ({
      ...prev,
      startDate,
      endDate: calculateEndDate(startDate, prev.period)
    }));
  };

  return (
    <Modal 
      isOpen={true}
      onClose={onClose} 
      size="max-w-2xl"
      showCloseButton={false}
    >
      {/* Modern Header */}
      <div className="relative -m-6 mb-6 rounded-t-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 p-4 sm:p-6">
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
          <div className="relative flex items-center justify-between">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white">
                {goal ? 'Edit Performance Goal' : 'Create Performance Goal'}
              </h3>
              <p className="text-blue-100 mt-1 text-sm sm:text-base">
                {goal ? 'Update goal details and settings' : 'Set up a new performance target'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
            >
              <XMarkIcon className="h-5 sm:h-6 w-5 sm:w-6" />
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-800">Goal Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
              required
            >
              {goalTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-800">Period</label>
            <select
              value={formData.period}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
              required
            >
              {periods.map(period => (
                <option key={period.value} value={period.value}>{period.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-800">Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
            placeholder="Enter goal title..."
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-800">Target Value</label>
            <input
              type="number"
              value={formData.targetValue}
              onChange={(e) => setFormData(prev => ({ ...prev, targetValue: parseInt(e.target.value) || 1 }))}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
              min="1"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-800">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-800">Start Date</label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-800">End Date</label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-800">Assigned To</label>
          <select
            value={formData.assignedTo}
            onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 text-sm sm:text-base"
          >
            <option value="">Company Goal (All Users)</option>
            {users.map(user => (
              <option key={user._id} value={user._id}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-800">Description <span className="text-gray-500 font-normal">(Optional)</span></label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/50 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 resize-none text-sm sm:text-base"
            rows={3}
            placeholder="Add any additional details or notes..."
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 pt-4 sm:pt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 sm:px-6 py-2 sm:py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium shadow-sm hover:shadow-md text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="w-3 sm:w-4 h-3 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Saving...</span>
              </div>
            ) : (
              goal ? 'Update Goal' : 'Create Goal'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PerformanceGoals;